import { ButtonHTMLAttributes } from "react";
import { IconBox } from "../IconBox";
import { Text } from "../Text";
import { css, useTheme } from "@emotion/react";

type ToggleButton = {
  name?: IconName;
  rightIcon?: IconName | null;
  active?: boolean;
  interaction?: "hover";
  onRightIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function ToggleButton({
  children,
  active = false,
  interaction,
  name,
  rightIcon,
  onRightIconClick,
  ...props
}: ToggleButton) {
  const theme = useTheme();

  const { backgroundColor, iconColor, textColor, hover } = active
    ? {
      backgroundColor: theme.palette.secondary.hover,
      iconColor: theme.palette.secondary.main,
      textColor: theme.palette.text.primary,
      hover: {},
    }
    : {
      backgroundColor: "transparent",
      iconColor: theme.palette.grey["400"],
      textColor: theme.palette.text.disabled,
      hover: {
        iconColor: theme.palette.secondary.main,
        textColor: theme.palette.text.primary,
      },
    };

  return (
    <button
      css={css`
        display: flex;
        align-items: center;
        gap: ${theme.shape.radius.medium}px;
        width: 100%;
        padding: ${theme.shape.radius.medium}px 16px;
        border-radius: ${theme.shape.radius.medium}px;
        background-color: ${backgroundColor};

        .icon {
          color: ${iconColor};
        }

        .text {
          color: ${textColor};
          flex-grow: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-align: left;
        }

        &:hover,
        &[data-interaction="hover"] {
          .icon {
            color: ${hover.iconColor};
          }

          .text {
            color: ${hover.textColor};
          }
        }
      `}
      type="button"
      data-interaction={interaction}
      {...props}
    >
      {name && (
        <IconBox
          className="icon"
          size="medium"
          name={name}
          color={active ? iconColor : undefined}
        />
      )}
      {children && (
        <Text className="text" variant="body2">
          {children}
        </Text>
      )}
      {rightIcon && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2px;
            border-radius: ${theme.shape.radius.medium}px;
            background-color: transparent;
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
              background-color: ${theme.palette.grey["a100"]};
            }

            .right-icon {
              color: ${theme.palette.grey["400"]};
            }

            &:hover .right-icon {
              color: ${theme.palette.text.secondary};
            }
          `}
          onClick={(e) => {
            e.stopPropagation();
            onRightIconClick?.(e);
          }}
        >
          <IconBox className="right-icon" size="medium" name={rightIcon} />
        </div>
      )}
    </button>
  );
}